import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function KnowledgeBrowser() {
  const navigate = useNavigate();
  const { name } = useParams();
  const kbName = decodeURIComponent(name);
  
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFile, setSourceFile] = useState('全部');
  const [product, setProduct] = useState('全部');
  const [isManual, setIsManual] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [contentLoading, setContentLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchKBContent();
  }, [kbName]);

  const fetchKBContent = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/knowledge/content?name=${encodeURIComponent(kbName)}`);
      const data = await resp.json();
      if (data.success) {
        setFiles(data.files || []);
        const validItems = (data.items || []).filter(i => i.q && i.q.length > 3 && i.a && i.a.length > 3);
        setItems(validItems);
        setModules(data.modules || []);
        // 检测是否产品手册类型（无问答数据，但有模块结构）
        setIsManual(validItems.length === 0 && (data.files || []).length > 0);
      }
    } catch (e) {
      console.error('获取内容失败', e);
    } finally {
      setLoading(false);
    }
  };

  const readFileContent = async (file) => {
    setContentLoading(true);
    setFileContent('');
    try {
      const resp = await fetch(`${API_URL}/api/knowledge/manual/read?kb=${encodeURIComponent(kbName)}&file=${encodeURIComponent(file.name)}`);
      if (resp.headers.get('content-type')?.includes('application/pdf')) {
        // PDF文件，返回URL让浏览器显示
        setFileContent({ type: 'pdf', url: resp.url });
      } else {
        const data = await resp.json();
        if (data.success) {
          setFileContent(data);
        }
      }
    } catch (e) {
      console.error('读取文件失败', e);
    } finally {
      setContentLoading(false);
    }
  };

  const exportPdf = async (file) => {
    setExporting(true);
    try {
      const resp = await fetch(`${API_URL}/api/knowledge/manual/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kb: kbName, file: file.name })
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${file.title}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert('导出失败');
      }
    } catch (e) {
      console.error('导出PDF失败', e);
      alert('导出失败');
    } finally {
      setExporting(false);
    }
  };

  // 产品统计（ISM按产品分类）
  const productStats = useMemo(() => {
    const stats = {};
    for (const item of items) {
      const p = item.product || '其他';
      stats[p] = (stats[p] || 0) + 1;
    }
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  }, [items]);

  // 来源文件统计
  const sourceStats = useMemo(() => {
    const stats = {};
    for (const item of items) {
      const f = item._file || item.source || '未知';
      const shortName = f.split('/')[0].replace('.md', '').replace('.json', '');
      stats[shortName] = (stats[shortName] || 0) + 1;
    }
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  }, [items]);

  // 过滤
  const filteredItems = useMemo(() => {
    let result = items;
    if (product !== '全部') {
      result = result.filter(i => (i.product || '').includes(product));
    }
    if (sourceFile !== '全部') {
      result = result.filter(i => {
        const f = i._file || i.source || '';
        return f.includes(sourceFile);
      });
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(i =>
        (i.q && i.q.toLowerCase().includes(term)) ||
        (i.a && i.a.toLowerCase().includes(term)) ||
        (i.product && i.product.toLowerCase().includes(term)) ||
        (i.module && i.module.toLowerCase().includes(term))
      );
    }
    return result;
  }, [items, searchTerm, sourceFile, product]);

  // 手册文件过滤
  const filteredFiles = useMemo(() => {
    let result = files;
    if (selectedModule && selectedModule !== '全部') {
      result = result.filter(f => f.kbPath === selectedModule || f.name.startsWith(selectedModule + '/'));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(f => f.name.toLowerCase().includes(term) || f.title.toLowerCase().includes(term));
    }
    return result;
  }, [files, selectedModule, searchTerm]);

  if (loading) {
    return (
      <div className="knowledge-container">
        <div className="knowledge-loading">
          <div className="knowledge-spinner" />
          <p>加载知识库...</p>
        </div>
      </div>
    );
  }

  // 产品手册类型（无问答数据）
  if (isManual) {
    return (
      <div className="knowledge-container">
        <div className="kb-browse-header">
          <button className="kb-back-btn" onClick={() => navigate('/knowledge')}>← 返回</button>
          <h2>📘 {kbName}</h2>
          <span className="kb-count">{files.length} 个文档</span>
        </div>

        <div className="kb-search-bar">
          <input
            type="text"
            placeholder="搜索手册..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="kb-search-input"
          />
          {searchTerm && <button className="kb-search-clear" onClick={() => setSearchTerm('')}>✕</button>}
        </div>

        {/* 模块筛选 */}
        <div className="kb-module-tabs">
          <button className={`kb-module-tab ${selectedModule === null ? 'active' : ''}`} onClick={() => setSelectedModule(null)}>全部模块</button>
          {modules.filter(m => m.files.length > 0).map(m => (
            <button key={m.name} className={`kb-module-tab ${selectedModule === m.name ? 'active' : ''}`} onClick={() => setSelectedModule(m.name)}>
              {m.name} ({m.files.length})
            </button>
          ))}
        </div>

        {/* 文件列表或内容展示 */}
        {selectedFile ? (
          <div className="kb-manual-content">
            <div className="kb-manual-toolbar">
              <button className="kb-back-btn" onClick={() => { setSelectedFile(null); setFileContent(''); }}>
                ← 返回列表
              </button>
              <h3>{selectedFile.title}</h3>
              <div className="kb-manual-actions">
                {selectedFile.type === 'md' && (
                  <button className="kb-action-btn" onClick={() => exportPdf(selectedFile)} disabled={exporting}>
                    {exporting ? '导出中...' : '📥 导出PDF'}
                  </button>
                )}
              </div>
            </div>
            <div className="kb-manual-body">
              {contentLoading ? (
                <div className="knowledge-loading"><div className="knowledge-spinner" /></div>
              ) : fileContent.type === 'pdf' ? (
                <iframe src={fileContent.url} style={{ width: '100%', height: '80vh', border: 'none' }} />
              ) : fileContent.content ? (
                <div className="kb-md-content">{fileContent.content}</div>
              ) : (
                <p className="kb-error">无法加载内容</p>
              )}
            </div>
          </div>
        ) : (
          <div className="kb-manual-grid">
            {filteredFiles.map((f, idx) => (
              <div key={idx} className="kb-manual-card" onClick={() => { setSelectedFile(f); readFileContent(f); }}>
                <div className="kb-file-type">{f.type.toUpperCase()}</div>
                <div className="kb-file-info">
                  <div className="kb-file-title">{f.title}</div>
                  <div className="kb-file-meta">{f.size}</div>
                </div>
                {f.type === 'md' && <div className="kb-file-badge">可导出</div>}
              </div>
            ))}
            {filteredFiles.length === 0 && (
              <div className="kb-empty">暂无匹配的手册文件</div>
            )}
          </div>
        )}
      </div>
    );
  }

  // 问答型知识库
  return (
    <div className="knowledge-container">
      <div className="kb-browse-header">
        <button className="kb-back-btn" onClick={() => navigate('/knowledge')}>← 返回</button>
        <h2>📂 {kbName}</h2>
        <span className="kb-count">{items.length} 条知识</span>
      </div>

      <div className="kb-search-bar">
        <input
          type="text"
          placeholder="搜索问题、答案、产品、模块..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="kb-search-input"
        />
        {searchTerm && <button className="kb-search-clear" onClick={() => setSearchTerm('')}>✕</button>}
      </div>

      {/* ISM按产品筛选 */}
      {kbName === 'ISM' && productStats.length > 1 && (
        <div className="kb-product-filter">
          <span className="kb-filter-label">按产品筛选：</span>
          <div className="kb-filter-tags">
            <button className={`kb-filter-tag ${product === '全部' ? 'active' : ''}`} onClick={() => setProduct('全部')}>全部 ({items.length})</button>
            {productStats.slice(0, 15).map(([name, count]) => (
              <button key={name} className={`kb-filter-tag ${product === name ? 'active' : ''}`} onClick={() => setProduct(name)}>{name} ({count})</button>
            ))}
          </div>
        </div>
      )}

      <div className="kb-source-filter">
        <span className="kb-filter-label">按来源筛选：</span>
        <div className="kb-filter-tags">
          <button className={`kb-filter-tag ${sourceFile === '全部' ? 'active' : ''}`} onClick={() => setSourceFile('全部')}>全部 ({items.length})</button>
          {sourceStats.slice(0, 10).map(([name, count]) => (
            <button key={name} className={`kb-filter-tag ${sourceFile === name ? 'active' : ''}`} onClick={() => setSourceFile(name)}>{name} ({count})</button>
          ))}
        </div>
      </div>

      {(searchTerm || sourceFile !== '全部' || product !== '全部') ? (
        <div className="kb-search-results">
          <p className="kb-result-count">找到 {filteredItems.length} 条结果</p>
          {filteredItems.slice(0, 100).map((item, idx) => (
            <div key={idx} className="kb-item-card">
              <div className="kb-item-q">❓ {item.q}</div>
              <div className="kb-item-a">💡 {item.a}</div>
              <div className="kb-item-meta">
                {item.product && <span className="kb-tag">{item.product}</span>}
                {item.module && <span className="kb-tag kb-tag-module">{item.module}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="kb-stats-summary">
            <div className="kb-stat-pill">总条目 <strong>{items.length}</strong></div>
            {productStats.length > 1 && <div className="kb-stat-pill">产品版本 <strong>{productStats.length}</strong></div>}
          </div>
          <div className="kb-empty">
            <p>📚 共 {items.length} 条知识，请使用搜索或筛选查看内容</p>
          </div>
          {kbName === 'ISM' && productStats.length > 1 && (
            <div className="kb-source-list">
              <h4>按产品版本分布</h4>
              <div className="kb-source-items">
                {productStats.slice(0, 15).map(([name, count]) => (
                  <div key={name} className="kb-source-stat-item" onClick={() => setProduct(name)}>
                    <span className="kb-source-stat-name">{name}</span>
                    <span className="kb-source-stat-count">{count} 条</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}